import { MediaModel } from './media.model';
import { Commentable } from './mixins/commentable';
import { Model } from './model';
import { ModelsModule } from './models.module';
import { UserModel } from './user.model';
import { formatDistanceToNowShort } from '../functions/date/format-distance';
import { PostCommentsGQL } from '../graphql/comments/post-comments.generated';
import { DeletePostGQL } from '../graphql/posts/delete-post.generated';
import { PostGQL } from '../graphql/posts/post.generated';
import { UpdatePostGQL } from '../graphql/posts/update-post.generated';
import { firstValueFrom, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export class PostModel extends Commentable(Model) {
  /**
   * Reference to the delete graphql mutation.
   */
  protected _deletePostGQL = ModelsModule.injector.get(DeletePostGQL);

  /**
   * Reference to the post comments graphql query.
   */
  protected _postCommentsGQL = ModelsModule.injector.get(PostCommentsGQL);

  /**
   * Reference to the post service.
   */
  protected _get = ModelsModule.injector.get(PostGQL);

  /**
   * Reference to the post service.
   */
  protected _updatePostGQL = ModelsModule.injector.get(UpdatePostGQL);

  /**
   * The content of the model.
   */
  content?: string;

  /**
   * The created at timestamp.
   */
  created_at?: string;

  /**
   * The entity type of the post.
   */
  entity_type?: string;

  /**
   * The hashtags of the model.
   */
  hashtags?: any[];

  /**
   * The id of the model.
   */
  id: any;

  /**
   * The images of the model.
   */
  get images(): any[] {
    if (this.media && this.media.length) {
      return this.media.filter((m) => m.type === "images");
    }

    return [];
  }

  /**
   * The liked state of the model.
   */
  is_liked?: boolean;

  /**
   * The number of likes of of the model.
   */
  likes_count?: number;

  /**
   * The users the post has been liked by.
   */
  likedByFollowing?: { data: UserModel[] };

  /**
   * The links of the model.
   */
  links?: any[];

  /**
   * The media of the model.
   */
  _media?: MediaModel[] | undefined;

  get media(): MediaModel[] | undefined {
    return this._media;
  }

  set media(value) {
    this._media = value?.map((media) => new MediaModel(media));
  }

  /**
   * The mentions of the model.
   */
  mentions?: any[];

  /**
   * The published at timestamp.
   */
  published_at?: string;

  /**
   * The raw content of the model.
   */
  raw_content?: string;

  /**
   * The route of the model.
   */
  route?: any[];

  /**
   * The shrared data of the post.
   */
  shareable: any;

  /**
   * The shareable type of the model.
   */
  shareable_type?: string;

  /**
   * The status of the post.
   */
  status?: string;

  /**
   * The time ago string.
   */
  get timeAgo(): any {
    if (this.published_at) {
      return formatDistanceToNowShort(new Date(this.published_at));
    }
  }

  /**
   * The user of the model.
   */
  user: any;

  /**
   * Handle model booted.
   */
  booted() {
    this.route = ["/posts", this.id];
  }

  /**
   * Delete the current model and remove it from the cache.
   */
  delete(): Promise<any> {
    return firstValueFrom(
      this._deletePostGQL.mutate(
        { id: this.id },
        {
          update: (cache, { data }) => {
            if (data?.deletePost) {
              cache.evict({ id: cache.identify(data?.deletePost) });
            }
          },
        }
      )
    );
  }

  /**
   * Get the comments of the model.
   */
  async getComments() {
    const {
      data: { postComments },
    } = await firstValueFrom(this._postCommentsGQL.fetch({ id: this.id }));

    this.comments = postComments;
  }

  /**
   * Get more comments for the model.
   */
  async getMoreComments(): Promise<any> {
    this.loadingComments = true;

    if (!this.comments) {
      return await this.getComments();
    }

    if (!this.hasMoreComments) {
      this.loadingComments = false;
      return;
    }

    const { data } = await firstValueFrom(
      this._postCommentsGQL.fetch({
        id: this.id,
        after: this.comments.pageInfo.endCursor,
      })
    );

    this.comments = {
      ...this.comments,
      pageInfo: { ...data?.postComments?.pageInfo },
    };

    if (data?.postComments?.edges) {
      this.addComments(data?.postComments?.edges);
    }

    this.loadingComments = false;
  }

  /**
   * Refreh the current post.
   */
  refresh(params = {}): Observable<any> {
    return this._get
      .fetch(
        {
          id: this.id,
          ...params,
        },
        {
          fetchPolicy: "network-only",
        }
      )
      .pipe(
        map(({ data: { post } }) => {
          Object.assign(this, post);

          return this;
        })
      );
  }

  /**
   * Update the post.
   */
  async update(input: any = {}): Promise<any> {
    return firstValueFrom(
      this._updatePostGQL.mutate({ id: this.id, input: input }).pipe(
        tap(({ data }) => {
          Object.assign(this, data?.updatePost);
        })
      )
    );
  }
}
